import { Injectable } from '@angular/core';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser';
import { loginRequest, msalConfig, silentRequest } from './auth-config';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msal = new PublicClientApplication(msalConfig);
  private initialized = false;
  
  get activeAccount(): AccountInfo | null {
    return this.msal.getActiveAccount();
  }

  get instance(): PublicClientApplication {
    return this.msal;
  }

  /**
   * Boot-time entry point (called from an APP_INITIALIZER).
   *
   * Order of operations:
   *  1. initialize the MSAL instance.
   *  2. finish any redirect that is coming back from Entra.
   *  3. reuse an already-cached account for this origin, if present.
   *  4. otherwise try a *silent* SSO using the Entra session cookie that AgriFinOps App
   *     established.
   *  5. only if silent SSO fails do we fall back to an interactive redirect.
   *
   * Resolves once an active account is set. If an interactive redirect is
   * triggered the page navigates away and this promise never resolves — which
   * is fine, the app simply re-boots after the round trip.
   */
  async initialize(): Promise<void> {
    if (this.initialized) { return; }
    await this.msal.initialize();
    this.initialized = true;

    // 1. Read the login hint forwarded by AgriFinOps App, then immediately remove it from
    // the address bar. We've captured it locally, so no auth path below needs
    // it to stay in the URL — this keeps the username out of the visible URL
    // regardless of which branch establishes the session.
    const loginHint = this.readLoginHint();
    this.stripLoginHintFromUrl();

    // 2. Complete a redirect coming back from Entra (login / token).
    const redirectResult = await this.msal.handleRedirectPromise();
    if (redirectResult?.account) {
      this.msal.setActiveAccount(redirectResult.account);
      return;
    }

    // 3. Reuse a cached account for this origin.
    const cached = this.msal.getActiveAccount() ?? this.msal.getAllAccounts()[0];
    if (cached) {
      this.msal.setActiveAccount(cached);
      return;
    }

    // 4. Silent SSO off the shared Entra session
    try {
      const result: AuthenticationResult = await this.msal.ssoSilent({
        ...silentRequest,
        ...(loginHint ? { loginHint } : {})
      });
      this.msal.setActiveAccount(result.account);
      return;
    } catch {
      // 5. Silent path unavailable (no session, consent needed, iframe blocked)
      //    for any reason — fall back to an interactive redirect sign-in.
      await this.msal.loginRedirect({
        ...loginRequest,
        ...(loginHint ? { loginHint } : {})
      });
    }
  }

  /** Acquire an access token for the given scopes, silently when possible. */
  async acquireToken(scopes: string[]): Promise<string | null> {
    const account = this.activeAccount;
    if (!account) { return null; }
    try {
      const result = await this.msal.acquireTokenSilent({ account, scopes });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.msal.acquireTokenRedirect({ scopes, account });
      }
      return null;
    }
  }

  getUsername(): string | null {
    return this.activeAccount?.username ?? null;
  }

  logout(): Promise<void> {
    return this.msal.logoutRedirect();
  }
  
  private readLoginHint(): string | null {
    try {
      return new URLSearchParams(window.location.search).get('login_hint');
    } catch {
      return null;
    }
  }

  /** Remove the login_hint query param so it doesn't linger in the address bar. */
  private stripLoginHintFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('login_hint')) {
        url.searchParams.delete('login_hint');
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    } catch {
      /* no-op */
    }
  }
}
